import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Columns3 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useT } from '@/lib/i18n';
import type { ResponseMeta } from '@/lib/types';
import { cn } from '@/lib/utils';
import { EmptyState, ErrorState, TableSkeleton } from './page';

export type Column<T> = {
  key: string;
  header: string;
  /** Sort key sent to the API; omit for a column that cannot be sorted. */
  sortKey?: string;
  cell: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  /** Starts hidden; the person can switch it on from "Edit columns". */
  hidden?: boolean;
};

type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  meta?: ResponseMeta;
  onPageChange?: (page: number) => void;
  /** Shows the 25 / 50 / 100 per page picker. */
  onLimitChange?: (limit: number) => void;
  onRowClick?: (row: T) => void;
  sort?: string;
  onSortChange?: (sort: string) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /**
   * Turns on row checkboxes. The bar above the table shows what to do with
   * the ticked rows; it gets the ids and a way to clear the selection.
   */
  bulkActions?: (selected: string[], clear: () => void) => ReactNode;
  /** Remembers which columns this person hid, per list. */
  storageKey?: string;
  /** Rendered flush inside a card, without its own border. */
  bare?: boolean;
};

/** Columns this person switched on or off, per list; the rest follow their default. */
function loadChoices(key: string | undefined): Record<string, boolean> {
  if (!key) return {};
  try {
    const raw = localStorage.getItem(`dovo.columns.v2.${key}`);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

/**
 * The list view used across every module: server-side pagination and
 * sorting, row selection with bulk actions, and a column picker — with the
 * loading, error and empty states handled once rather than per screen.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  onRetry,
  meta,
  onPageChange,
  onLimitChange,
  onRowClick,
  sort,
  onSortChange,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  bulkActions,
  storageKey,
  bare,
}: DataTableProps<T>) {
  const t = useT();
  const [choices, setChoices] = useState<Record<string, boolean>>(() => loadChoices(storageKey));
  const isShown = (column: Column<T>) => choices[column.key] ?? !column.hidden;
  const [selected, setSelected] = useState<string[]>([]);

  // A new page or filter starts with nothing ticked.
  const pageKeys = rows.map(rowKey).join('|');
  useEffect(() => setSelected([]), [pageKeys]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visible = useMemo(() => columns.filter(isShown), [columns, choices]);

  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (isLoading) return <TableSkeleton columns={columns.length} />;
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} className={bare ? 'border-0' : undefined} />;
  }

  const toggleSort = (sortKey: string) => {
    if (!onSortChange) return;
    // Ascending, then descending, then back to the default.
    if (sort === sortKey) onSortChange(`-${sortKey}`);
    else if (sort === `-${sortKey}`) onSortChange('');
    else onSortChange(sortKey);
  };

  const toggleColumn = (column: Column<T>) => {
    const next = { ...choices, [column.key]: !isShown(column) };
    // Never hide the last column.
    if (!columns.some((item) => next[item.key] ?? !item.hidden)) return;
    setChoices(next);
    if (storageKey) {
      try {
        localStorage.setItem(`dovo.columns.v2.${storageKey}`, JSON.stringify(next));
      } catch {
        // Not remembered.
      }
    }
  };

  const ids = rows.map(rowKey);
  const allTicked = ids.length > 0 && ids.every((id) => selected.includes(id));
  const someTicked = selected.length > 0 && !allTicked;
  const clear = () => setSelected([]);

  return (
    <div className="space-y-3">
      <div className={cn('overflow-hidden bg-card', !bare && 'rounded-lg border')}>
        {bulkActions && selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-primary/[0.05] px-3 py-2 text-sm">
            <span className="font-semibold">
              {selected.length} {t('selected')}
            </span>
            <button type="button" onClick={clear} className="text-link text-xs font-semibold hover:underline">
              {t('Clear')}
            </button>
            <span className="mx-1 h-4 w-px bg-border" />
            <div className="flex flex-wrap items-center gap-1.5">{bulkActions(selected, clear)}</div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {bulkActions && (
                <TableHead className="w-10 pe-0">
                  <Checkbox
                    checked={allTicked ? true : someTicked ? 'indeterminate' : false}
                    onCheckedChange={() => setSelected(allTicked ? [] : ids)}
                    aria-label="Select all"
                  />
                </TableHead>
              )}
              {visible.map((column) => (
                <TableHead key={column.key} className={column.headerClassName}>
                  {column.sortKey && onSortChange ? (
                    <button
                      onClick={() => toggleSort(column.sortKey!)}
                      className="group inline-flex items-center gap-1 hover:text-foreground"
                    >
                      {t(column.header)}
                      {sort === column.sortKey ? (
                        <ArrowUp className="h-3.5 w-3.5 text-primary" />
                      ) : sort === `-${column.sortKey}` ? (
                        <ArrowDown className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <ChevronsUpDown className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-50" />
                      )}
                    </button>
                  ) : (
                    t(column.header)
                  )}
                </TableHead>
              ))}
              <TableHead className="w-10 px-2 text-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                      title={t('Edit columns')}
                    >
                      <Columns3 className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-[60vh] w-60 overflow-y-auto">
                    <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      {t('Columns')}
                    </div>
                    {columns.map((column) => (
                      <label
                        key={column.key}
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <Checkbox checked={isShown(column)} onCheckedChange={() => toggleColumn(column)} />
                        {t(column.header) || column.key}
                      </label>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const id = rowKey(row);
              const ticked = selected.includes(id);
              return (
                <TableRow
                  key={id}
                  data-state={ticked ? 'selected' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(onRowClick && 'cursor-pointer')}
                >
                  {bulkActions && (
                    <TableCell className="w-10 pe-0" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={ticked}
                        onCheckedChange={() =>
                          setSelected((current) => (ticked ? current.filter((value) => value !== id) : [...current, id]))
                        }
                        aria-label="Select row"
                      />
                    </TableCell>
                  )}
                  {visible.map((column) => (
                    <TableCell key={column.key} className={column.className}>
                      {column.cell(row)}
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {meta && onPageChange && (meta.totalPages > 1 || (onLimitChange && meta.total > 25)) && (
        <Pager meta={meta} onPageChange={onPageChange} onLimitChange={onLimitChange} />
      )}
    </div>
  );
}

/** Previous, a few page numbers around the current one, next. */
function Pager({ meta, onPageChange, onLimitChange }: { meta: ResponseMeta; onPageChange: (page: number) => void; onLimitChange?: (limit: number) => void }) {
  const t = useT();
  const pages: number[] = [];
  const from = Math.max(1, Math.min(meta.page - 2, meta.totalPages - 4));
  for (let page = from; page <= Math.min(meta.totalPages, from + 4); page += 1) pages.push(page);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
      <span className="tabular flex items-center gap-3">
        {(meta.page - 1) * meta.limit + 1}–{Math.min(meta.page * meta.limit, meta.total)} {t('of')} {meta.total}
        {onLimitChange && (
          <select
            value={meta.limit}
            onChange={(event) => onLimitChange(Number(event.target.value))}
            className="h-8 rounded-md border bg-card px-2 text-xs font-semibold text-foreground"
            aria-label={t('Records per page')}
          >
            {[25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} {t('per page')}
              </option>
            ))}
          </select>
        )}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" disabled={meta.page <= 1} onClick={() => onPageChange(meta.page - 1)}>
          <ChevronLeft className="h-4 w-4" />
          {t('Prev')}
        </Button>
        {pages.map((page) => (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            className={cn(
              'tabular h-8 min-w-8 rounded-md px-2 text-sm font-semibold',
              page === meta.page ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'hover:bg-accent hover:text-foreground',
            )}
          >
            {page}
          </button>
        ))}
        <Button variant="ghost" size="sm" disabled={meta.page >= meta.totalPages} onClick={() => onPageChange(meta.page + 1)}>
          {t('Next')}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
